import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { color as chartColor } from "chart.js/helpers"
import { customTooltips } from "@coreui/chartjs"
import { getStyle } from "@coreui/utils"

const palette = ["primary", "info", "warning", "success", "danger"]
const arcTypes = ["doughnut", "pie", "polarArea"]
const pointTypes = ["scatter", "bubble"]
const bodyColor = () => getStyle("--cui-body-color")
const gridColor = () => getStyle("--cui-border-color")
const bodyBackground = () => getStyle("--cui-body-bg")

export default class ChartsController extends Controller {
  static targets = [
    "line",
    "bar",
    "doughnut",
    "radar",
    "pie",
    "polarArea",
    "scatter",
    "bubble",
    "stackedArea",
    "mixed",
    "tooltipStyle",
    "paletteButton",
  ]

  connect() {
    this.coreuiPalette = false
    this.charts = [
      this.createLineChart(),
      this.createBarChart(),
      this.createDoughnutChart(),
      this.createRadarChart(),
      this.createPieChart(),
      this.createPolarAreaChart(),
      this.createScatterChart(),
      this.createBubbleChart(),
      this.createStackedAreaChart(),
      this.createMixedChart(),
    ]
    this.changeTooltipStyle()
    document.documentElement.addEventListener("ColorSchemeChange", this.updateTheme)
  }

  disconnect() {
    this.teardown()
  }

  teardown() {
    document.documentElement.removeEventListener("ColorSchemeChange", this.updateTheme)
    this.charts?.forEach((chart) => chart.destroy())
    this.charts = []
    this.element.querySelectorAll(".chartjs-tooltip").forEach((tooltip) => tooltip.remove())
  }

  createLineChart() {
    const data = this.chartData(this.lineTarget)
    data.datasets.forEach((dataset) => Object.assign(dataset, { fill: true, tension: 0.3 }))

    return this.createChart(this.lineTarget, {
      type: "line",
      data,
      options: {
        ...this.chartOptions("index"),
        scales: this.cartesianScales(),
      },
    })
  }

  createBarChart() {
    const options = this.chartOptions("index")
    options.plugins.colors = { enabled: true, forceOverride: true }
    options.scales = this.cartesianScales()

    return this.createChart(this.barTarget, {
      type: "bar",
      data: this.chartData(this.barTarget),
      options,
    })
  }

  createDoughnutChart() {
    const data = this.chartData(this.doughnutTarget)
    data.datasets.forEach((dataset) => {
      dataset.borderRadius = 5
      dataset.spacing = 3
    })

    return this.createChart(this.doughnutTarget, {
      type: "doughnut",
      data,
      options: { ...this.chartOptions(), cutout: "65%" },
    })
  }

  createRadarChart() {
    const data = this.chartData(this.radarTarget)
    data.datasets.forEach((dataset) => Object.assign(dataset, { fill: true, tension: 0.3 }))

    return this.createChart(this.radarTarget, {
      type: "radar",
      data,
      options: {
        ...this.chartOptions(),
        scales: { r: { ...this.radialScale(), max: 100 } },
      },
    })
  }

  createPieChart() {
    return this.createChart(this.pieTarget, {
      type: "pie",
      data: this.chartData(this.pieTarget),
      options: this.chartOptions(),
    })
  }

  createPolarAreaChart() {
    return this.createChart(this.polarAreaTarget, {
      type: "polarArea",
      data: this.chartData(this.polarAreaTarget),
      options: {
        ...this.chartOptions(),
        scales: { r: this.radialScale() },
      },
    })
  }

  createScatterChart() {
    const axes = JSON.parse(this.scatterTarget.dataset.chartPointAxes)
    const options = this.chartOptions()
    options.elements.point.radius = 5
    options.scales = this.cartesianScales()
    Object.assign(options.scales.x, {
      type: "linear",
      grace: "5%",
      title: { display: true, text: axes.x, color: bodyColor },
    })
    options.scales.y.title = { display: true, text: axes.y, color: bodyColor }
    options.plugins.tooltip.callbacks = {
      label: (context) => `${context.dataset.label}: ${context.raw.x} min · ${context.raw.y} rentals`,
    }

    return this.createChart(this.scatterTarget, {
      type: "scatter",
      data: this.chartData(this.scatterTarget),
      options,
    })
  }

  createBubbleChart() {
    const axes = JSON.parse(this.bubbleTarget.dataset.chartPointAxes)
    const options = this.chartOptions()
    options.scales = this.cartesianScales()
    Object.assign(options.scales.x, {
      type: "linear",
      grace: "5%",
      title: { display: true, text: axes.x, color: bodyColor },
    })
    options.scales.y.title = { display: true, text: axes.y, color: bodyColor }
    options.plugins.tooltip.callbacks = {
      label: (context) =>
        `${context.dataset.label}: ${context.raw.x} min · ${context.raw.y} rentals · ${context.raw.r} copies`,
    }

    return this.createChart(this.bubbleTarget, {
      type: "bubble",
      data: this.chartData(this.bubbleTarget),
      options,
    })
  }

  createStackedAreaChart() {
    const data = this.chartData(this.stackedAreaTarget)
    Object.assign(data.datasets[0], { fill: true, tension: 0.3 })
    Object.assign(data.datasets[1], { fill: "-1", tension: 0.3 })
    const scales = this.cartesianScales()
    scales.x.stacked = true
    scales.y.stacked = true

    return this.createChart(this.stackedAreaTarget, {
      type: "line",
      data,
      options: { ...this.chartOptions("index"), scales },
    })
  }

  createMixedChart() {
    const data = this.chartData(this.mixedTarget)
    Object.assign(data.datasets[0], { type: "bar", order: 2 })
    Object.assign(data.datasets[1], { type: "line", order: 1, fill: false, tension: 0.3 })

    return this.createChart(this.mixedTarget, {
      type: "line",
      data,
      options: {
        ...this.chartOptions("index"),
        scales: this.cartesianScales(),
      },
    })
  }

  // Rails supplies the same sample values to each canvas and its accessible table.
  // When reusing an example, replace chartData(...) with your own labels and datasets.
  chartData(canvas) {
    return JSON.parse(canvas.dataset.chartData)
  }

  createChart(canvas, configuration) {
    Chart.getChart(canvas)?.destroy()
    this.colorDatasets(configuration.type, configuration.data.datasets)
    return new Chart(canvas, configuration)
  }

  chartOptions(mode = "nearest") {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? false : { duration: 250 },
      color: bodyColor,
      interaction: { mode, intersect: false },
      elements: { point: { radius: 3, hoverRadius: 5, hitRadius: 8 } },
      plugins: {
        colors: { enabled: false },
        legend: { position: "bottom", labels: { color: bodyColor, usePointStyle: true, boxWidth: 10, padding: 18 } },
        tooltip: {
          enabled: false,
          // Explicit options avoid inheriting defaults changed by dashboard demos after Turbo visits.
          mode,
          position: "average",
          backgroundColor: () => getStyle("--cui-emphasis-color"),
          titleColor: bodyBackground,
          bodyColor: bodyBackground,
          external: (context) => this.renderTooltip(context),
        },
      },
    }
  }

  cartesianScales() {
    return {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: bodyColor } },
      y: { beginAtZero: true, grid: { color: gridColor }, border: { display: false }, ticks: { color: bodyColor } },
    }
  }

  radialScale() {
    return {
      beginAtZero: true,
      grid: { color: gridColor },
      angleLines: { color: gridColor },
      pointLabels: { color: bodyColor },
      ticks: { color: bodyColor, backdropColor: bodyBackground },
    }
  }

  colorDatasets(type, datasets) {
    // Leave the bar datasets unstyled so Chart.js's Colors plugin supplies its palette.
    if (type === "bar" && !this.coreuiPalette) return

    const colors = palette.map((name) => getStyle(`--cui-${name}`))
    datasets.forEach((dataset, index) => {
      const color = colors[index % colors.length]
      const datasetType = dataset.type || type
      if (arcTypes.includes(type)) {
        dataset.backgroundColor =
          type === "polarArea" ? colors.map((value) => chartColor(value).alpha(0.65).rgbString()) : colors
        dataset.borderColor = bodyBackground()
        dataset.borderWidth = 2
        dataset.hoverOffset = 6
      } else {
        dataset.borderColor = color
        dataset.backgroundColor = chartColor(color)
          .alpha(datasetType === "bar" || pointTypes.includes(datasetType) ? 0.65 : 0.12)
          .rgbString()
        dataset.borderWidth = 2
        if (datasetType === "line" || datasetType === "radar") {
          dataset.pointBackgroundColor = color
          dataset.pointBorderColor = bodyBackground()
        }
      }
    })
  }

  changeTooltipStyle() {
    const style = this.tooltipStyleTarget.value
    this.element.classList.toggle("charts-page--accent", style === "accent")
    this.element.querySelectorAll(".chartjs-tooltip").forEach((tooltip) => tooltip.remove())
    this.charts.forEach((chart) => {
      chart.options.plugins.tooltip.enabled = style === "canvas"
      chart.setActiveElements([])
      chart.tooltip.setActiveElements([], { x: 0, y: 0 })
      chart.update("none")
    })
  }

  togglePalette() {
    this.coreuiPalette = !this.coreuiPalette
    this.paletteButtonTarget.setAttribute("aria-pressed", String(this.coreuiPalette))
    this.updateTheme()
  }

  updateTheme = () => {
    this.charts?.forEach((chart) => {
      this.colorDatasets(chart.config.type, chart.data.datasets)
      if (chart.config.type === "bar") chart.options.plugins.colors.enabled = !this.coreuiPalette
      chart.update("none")
    })
  }

  renderTooltip(context) {
    if (this.tooltipStyleTarget.value === "canvas") return

    customTooltips(context)
    const wrapper = context.chart.canvas.parentElement
    const tooltip = wrapper.querySelector(".chartjs-tooltip")
    tooltip.setAttribute("role", "tooltip")
    // Let the package's CSS variables control padding instead of inline styles.
    tooltip.style.removeProperty("padding")
    if (context.tooltip.opacity === 0) return

    // Keep the HTML tooltip within its chart on narrow screens and at edge points.
    const halfWidth = tooltip.offsetWidth / 2
    const left = context.chart.canvas.offsetLeft + context.tooltip.caretX
    tooltip.style.left = `${Math.max(halfWidth, Math.min(left, wrapper.clientWidth - halfWidth))}px`
  }
}
